// ------------------ IMPORTS -------------------

// Utils / Config / Services
import { endpointService } from "#src/services/endpoint";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { createToast } from "#src/common/system/toasts";

// Components
import { TableActionLabelV2 } from "#src/commonV2/Table/TableActionLabel";

// Others
import { Dispatch, ReactNode, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit02, Trash01 } from "@untitled-ui/icons-react";
import { IPassport } from "#src/services/endpoint/passports/types";

// ------------------ TYPES -------------------

interface UsePassportTableActionsProps {
  workspaceId: string;
  setPassportList: Dispatch<SetStateAction<IPassport[]>>;
  setIsAddEditModalOpen: (open: boolean) => void;
  setIsDeleteModalOpen: (open: boolean) => void;
  setSelectedPassport: (passport: IPassport | null) => void;
  canEdit: boolean;
}

interface UsePassportTableActionsReturn {
  switchingItem: string | null;
  getTableActions: (record: IPassport) => {
    key: string;
    label: ReactNode;
    action: () => void;
  }[];
  onToggleStatus: (record: IPassport) => Promise<void>;
  onSwapPriority: (draggingItem: IPassport, endItem: IPassport | null) => void;
}

// ------------------ HOOK -------------------

export const usePassportTableActions = (
  props: UsePassportTableActionsProps
): UsePassportTableActionsReturn => {
  const {
    workspaceId,
    setPassportList,
    setIsAddEditModalOpen,
    setIsDeleteModalOpen,
    setSelectedPassport,
    canEdit,
  } = props;

  const { t } = useTranslation("endpoint", { keyPrefix: "passports" });

  // ------------------ STATE -------------------

  const [switchingItem, setSwitchingItem] = useState<string | null>(null);

  // ------------------ METHODS -------------------

  // Action list for kebab menu
  const getTableActions = (record: IPassport) => {
    const actions: {
      key: string;
      label: ReactNode;
      action: () => void;
    }[] = [
      {
        key: "edit",
        label: (
          <TableActionLabelV2
            icon={<Edit02 className="h-5 w-5" />}
            text={t("table.action.edit")}
          />
        ),
        action: () => {
          // Open edit modal
          setSelectedPassport(record);
          setIsAddEditModalOpen(true);
        },
      },
      {
        key: "delete",
        label: (
          <TableActionLabelV2
            icon={<Trash01 className="h-5 w-5" />}
            text={t("table.action.delete")}
            warning
          />
        ),
        action: () => {
          // Open delete modal
          setSelectedPassport(record);
          setIsDeleteModalOpen(true);
        },
      },
    ];

    return actions;
  };

  // Toggle status (enable/disable)
  const onToggleStatus = async (record: IPassport) => {
    // If current user is not WORKSPACE OWNER, they should not be able to make changes.
    if (!canEdit) {
      return;
    }

    setSwitchingItem(record.id);

    try {
      await endpointService.updatePassport(workspaceId, record.id, {
        ...record,
        enabled: !record.enabled,
      });
      createToast({
        type: "success",
        message: t("table.notification.updateStatus.success"),
      });
      setPassportList((prev) =>
        prev.map((item) =>
          item.id === record.id ? { ...item, enabled: !item.enabled } : item
        )
      );
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setSwitchingItem(null);
    }
  };

  // Swap priority
  const onSwapPriority = (
    draggingItem: IPassport,
    endItem: IPassport | null
  ) => {
    createToast({
      type: "info",
      message: t("table.notification.changePriority.start.title"),
      detail: t("table.notification.changePriority.start.message"),
    });
    endpointService
      .changePassportPriority(workspaceId, draggingItem.id, endItem?.id || null)
      .then(() => {
        createToast({
          type: "success",
          message: t("table.notification.changePriority.success.title"),
        });
        // updatePassportList();
      })
      .catch((error) => {
        // Revert the list to the original state
        setPassportList((prev) => {
          const newList = [...prev];
          return newList;
        });

        apiErrorHandler(error);
      });
  };

  return {
    switchingItem,
    getTableActions,
    onToggleStatus,
    onSwapPriority,
  };
};
